import { TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import React from 'react';
import { Link } from 'react-router';
import { termDetailPath, termRouteReference } from '../GovernanceTaxonomyManagement.utils';
import { MUTED_TERM_CLASS_NAME, termStatusPresentation } from './termStatusPresentation';
import {
  DisclosedRival,
  disclosedRivals,
  RivalTermMatch,
  shouldDiscloseUntrackedRivals,
  untrackedRivalChipLabel,
  UntrackedRival,
} from './untrackedRivalDisclosure.utils';
import { RivalTermSource, useRivalTermMatches } from './useRivalTermMatches';

/**
 * The disclosure beside a 100% share of voice.
 *
 * A monopoly reading is the one number here that misleads by being correct: when an answer names
 * rivals the customer does not track, the tracked population is one brand, so its share genuinely
 * is 100% — and it reads as "we own this conversation" rather than "we are the only one counted".
 *
 * Muted on purpose. This is a caveat on someone else's number, not a finding of its own; at full
 * strength it would compete with the figure it qualifies.
 */

/** How a matched term's status reads in the details list. */
function statusLabel(status: TaxonomyTermStatus | undefined): string | undefined {
  if (!status) {
    return undefined;
  }
  return status === TaxonomyTermStatus.Archived ? 'archived' : status === TaxonomyTermStatus.Active ? 'tracked' : 'suggested';
}

/** One rival in the details list: a link when we know the term, plain text when we do not. */
function RivalRow({ rival }: { rival: DisclosedRival }) {
  const match: RivalTermMatch | undefined = rival.match;
  const presentation = termStatusPresentation(match?.status, 'table-row', { neverMute: true });
  const label = statusLabel(match?.status);

  // The term's key where it has one, its id otherwise — the same reference every other link to a
  // term's page carries.
  const reference = termRouteReference({ id: match?.termId, key: match?.termKey });

  const name =
    reference && match?.facet ? (
      <Link to={termDetailPath(match.facet, reference)} className="text-sky-700 hover:underline">
        {rival.name}
      </Link>
    ) : (
      // No term matched, so there is nothing true to say beyond the name itself.
      <span className="text-slate-700">{rival.name}</span>
    );

  return (
    <li className={['flex items-center gap-2 text-xs', presentation.className].filter(Boolean).join(' ')}>
      {name}
      {label ? <span className="text-slate-400">{label}</span> : null}
      {typeof match?.seenInRuns === 'number' ? (
        <span className="text-slate-400 whitespace-nowrap">
          seen in {match.seenInRuns} {match.seenInRuns === 1 ? 'answer' : 'answers'}
        </span>
      ) : null}
      {rival.reconsider ? (
        // The actionable insight: re-approving this term is what makes the share of voice
        // competitive again, which is the whole reason the chip funnels operators here.
        <span className="text-amber-700 font-semibold whitespace-nowrap">reconsider</span>
      ) : null}
    </li>
  );
}

export function UntrackedRivalsChip({
  api,
  sharePercent,
  rivals,
}: {
  /** Only the taxonomy-term lookup is used; the full client satisfies this structurally. */
  api?: RivalTermSource;
  /** The share-of-voice figure this chip qualifies. */
  sharePercent?: number;
  /** The run's untracked co-subjects. */
  rivals: readonly UntrackedRival[];
}) {
  const [open, setOpen] = React.useState(false);
  const disclose = shouldDiscloseUntrackedRivals(sharePercent, rivals);
  // Only fetched once the chip is actually shown: an answer with a competitive share never pays
  // for a lookup it will not use.
  const termMatches = useRivalTermMatches(api, disclose);

  if (!disclose) {
    return null;
  }

  const disclosed = disclosedRivals(rivals, termMatches);

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        className={`text-[11px] rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-slate-500 hover:bg-slate-100 ${MUTED_TERM_CLASS_NAME}`}
        title="This answer named rivals that are not tracked, so the share above counts only your own brand."
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        {untrackedRivalChipLabel(disclosed.length)}
      </button>

      {open ? (
        <div
          className="absolute z-20 top-full left-0 mt-1 w-64 rounded-md border border-slate-200 bg-white p-2 shadow-sm cursor-auto"
          role="dialog"
          aria-label="Untracked rivals in this answer"
          // This panel is nested inside a row that pins the answer's highlighting when clicked.
          // Reading a disclosure is not choosing a subject, so its clicks stop here — and the
          // cursor reverts, because the panel itself is not the clickable thing.
          onClick={(event) => event.stopPropagation()}
        >
          <div className="text-[11px] text-slate-500 mb-1">Named in this answer but not counted in the share above.</div>
          <ul className="list-none p-0 m-0 space-y-1">
            {disclosed.map((rival) => (
              <RivalRow key={rival.name} rival={rival} />
            ))}
          </ul>
        </div>
      ) : null}
    </span>
  );
}
